import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table, Switch } from 'antd'
import { wtGetFromLocalStorage, wtSaveToLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import { wtFormatRoundedValue } from 'src/helpers/commons/utils'
import {
  SALE_ORDER_CURRENCY_SYMBOL,
  SALE_ORDER_STATUS_DROPDOWN_OPTIONS,
  SALE_ORDER_STATUS_NAME,
} from 'src/models/sale-orders'
import WidgetDepartmentSelector from 'src/widgets/Selectors/WidgetDepartmentSelector'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import WidgetCustomerSelector from 'src/widgets/Selectors/WidgetCustomerSelector'
import WidgetAccountsSelector from 'src/widgets/Selectors/WidgetAccountsSelector'
import style from './style.css'
import { useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'
import dayjs from 'dayjs'
import jsPDF from 'jspdf'
import { SaleOrderDeliveryPDF } from 'src/PDF/SaleOrderDeliveryPDF'

const SaleOrderDeliveryReport = (props) => {
  const Base = new BaseListing('sale_order_report')
  const { formatDate, formatDateTime } = useMasterDateTimeFormats()
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [branchId, setBranchId] = useState(0)
  const [DepartmentId, setDepartmentId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [DepartmentName, setDepartmentName] = useState('')
  const [SoDData, setSaleOrderDeliveryReportData] = useState([])
  const [SoDSummary, setSaleOrderDeliveryReportSummary] = useState(null)
  const branches = wtGetFromLocalStorage('branches')
  const [customers, setCustomers] = useState([])
  const [departments, setDepartments] = useState([])
  const [projects, setProjects] = useState([])
  const [accounts, setAccounts] = useState([])
  const [ProjectId, setProjectId] = useState('')
  const [ProjectName, setProjectName] = useState('')
  const [RevenueAccountId, setRevenueAccountId] = useState(0)
  const [RevenueAccountName, setRevenueAccountName] = useState('')
  const [searchMore, setSearchMore] = useState(false)
  const [dateRange, setDateRange] = useState(
    props?.from_date && props?.to_date ? [dayjs(props.from_date), dayjs(props.to_date)] : [],
  )
  const [fromDate, setFromDate] = useState(props?.from_date || null)
  const [toDate, setToDate] = useState(props?.to_date || null)
  const [PartyName, setPartyName] = useState('')
  const [PartyId, setPartyId] = useState(props?.party_id ? +props.party_id : null)
  const [Status, setStatus] = useState(0)
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false)
  const [pdfProgress, setPdfProgress] = useState(null)

  const flattenTreeOptions = (nodes = []) =>
    nodes.flatMap((node) => [node, ...(node?.children ? flattenTreeOptions(node.children) : [])])

  const changeValues = (key, val) => {
    setSaleOrderDeliveryReportData([])
    setSaleOrderDeliveryReportSummary(null)
    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)
    }
    if (key === 'department_id') {
      setDepartmentId(val.value)
      setDepartmentName(val.label)
    }
    if (key === 'project_id') {
      setProjectId(val)
      const selectedProject = projects.find((p) => p.value === val)
      setProjectName(selectedProject ? selectedProject.title : '')
    }
    if (key === 'revenue_account_id') {
      setRevenueAccountId(val || 0)
      if (!val) {
        setRevenueAccountName('')
      }
    }
    if (key === 'date_range') {
      setDateRange(val)
    }
    if (key === 'type') {
      setVoucherType(val.value)
      setVoucherTypeName(val.label)
    }
    if (key === 'customer_id') {
      setPartyId(val)
      if (val === null) {
        setPartyName('')
      }
    }
    if (key === 'status') {
      setStatus(val.value)
    }
  }

  const exportToExcel = () => {
    setIsLoading(true)
    wtApiPostWithContentTypeHandling('sale_order_delivery_report/export', { data: SoDData })
    setIsLoading(false)
  }

  const printDoPdf = async () => {
    if (SoDData.length === 0) {
      lzNotifyErrorMessageFromResult('Please search the report first')
      return
    }

    setIsLoading(true)
    setPdfProgress({ stage: 'Loading DOs', current: 0, total: SoDData.length })

    try {
      const uniqueDeliveryIds = [...new Set(SoDData.map((row) => row.id).filter(Boolean))]
      const deliveryData = []
      const requestBatchSize = 6
      for (let index = 0; index < uniqueDeliveryIds.length; index += requestBatchSize) {
        const batchIds = uniqueDeliveryIds.slice(index, index + requestBatchSize)
        const batchData = await Promise.all(
          batchIds.map(
            (id) =>
              new Promise((resolve, reject) => {
                wtApiCall(
                  `sale_order_deliveries/${id}`,
                  {},
                  'get',
                  (response) => resolve(response.data),
                  reject,
                  '',
                  60000,
                )
              }),
          ),
        )
        deliveryData.push(...batchData)
        setPdfProgress({
          stage: 'Loading DOs',
          current: Math.min(index + batchIds.length, uniqueDeliveryIds.length),
          total: uniqueDeliveryIds.length,
        })
      }

      const doc = new jsPDF()
      for (const [index, delivery] of deliveryData.entries()) {
        SaleOrderDeliveryPDF(delivery, {
          doc,
          append: index > 0,
          onlyOrderConfirmationCopy: true,
          returnDoc: true,
        })
        setPdfProgress({ stage: 'Building PDF', current: index + 1, total: deliveryData.length })
        // Let the browser repaint between DOs so the page remains responsive.
        await new Promise((resolve) => setTimeout(resolve, 0))
      }

      setPdfProgress({ stage: 'Downloading PDF', current: 1, total: 1 })
      doc.save(`sale_order_delivery_report_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`)
    } catch (error) {
      lzNotifyErrorMessageFromResult(error)
    } finally {
      setIsLoading(false)
      setPdfProgress(null)
    }
  }

  useEffect(() => {
    Base.dropdownData(
      'customers,departments,projects,accounts',
      {},
      [setCustomers, setDepartments, setProjects, setAccounts],
      1,
    )
  }, [])

  useEffect(() => {
    const drilldown = wtGetFromLocalStorage('sale_order_delivery_report_drilldown', null)
    if (drilldown) {
      if (drilldown.from_date && drilldown.to_date) {
        setFromDate(drilldown.from_date)
        setToDate(drilldown.to_date)
        setDateRange([dayjs(drilldown.from_date), dayjs(drilldown.to_date)])
      }
      if (+drilldown.department_id > 0) {
        setDepartmentId(+drilldown.department_id)
      }
      if (drilldown.department_name) {
        setDepartmentName(drilldown.department_name)
      }
      if (+drilldown.revenue_account_id > 0) {
        setRevenueAccountId(+drilldown.revenue_account_id)
      }
      if (drilldown.revenue_account_name) {
        setRevenueAccountName(drilldown.revenue_account_name)
      }

      setShouldAutoSearch(true)
      wtSaveToLocalStorage('sale_order_delivery_report_drilldown', null)
    }

    if (props?.department_id && departments.length > 0) {
      const department = flattenTreeOptions(departments).find(
        (item) => +item.value === +props.department_id,
      )

      if (department) {
        setDepartmentId(+department.value)
        setDepartmentName(department.label)
        setShouldAutoSearch(true)
      }
    } else if (props?.department_id && props?.department_name) {
      setDepartmentId(+props.department_id)
      setDepartmentName(props.department_name)
      setShouldAutoSearch(true)
    }

    if (props?.revenue_account_id && accounts.length > 0) {
      const revenueAccount = flattenTreeOptions(accounts).find(
        (item) => +item.value === +props.revenue_account_id,
      )

      if (revenueAccount) {
        setRevenueAccountId(+revenueAccount.value)
        setRevenueAccountName(revenueAccount.label || revenueAccount.title || '')
        setShouldAutoSearch(true)
      }
    } else if (props?.revenue_account_id && props?.revenue_account_name) {
      setRevenueAccountId(+props.revenue_account_id)
      setRevenueAccountName(props.revenue_account_name)
      setShouldAutoSearch(true)
    }

    if (props?.branch_id && branches.length > 0) {
      const branch = branches.find((item) => +item.value === +props.branch_id)
      if (branch) {
        setBranchId(+branch.value)
        setBranchName(branch.label)
      }
    }

    if (props?.customer_id && customers.length > 0) {
      const customer = customers.find((item) => +item.value === +props.customer_id)
      if (customer) {
        setPartyId(+customer.value)
        setPartyName(customer.label)
      }
    }
  }, [departments, branches, customers, accounts, props, fromDate, toDate])

  useEffect(() => {
    if (!shouldAutoSearch) {
      return
    }

    if (dateRange.length < 2) {
      return
    }

    setShouldAutoSearch(false)
    getReportData()
  }, [shouldAutoSearch, dateRange, DepartmentId, RevenueAccountId, branchId, PartyId, Status, ProjectId])

  const getReportData = () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }
    const fromDate = dateRange[0]
    const toDate = dateRange[1]
    setIsLoading(true)
    setSaleOrderDeliveryReportSummary(null)
    wtApiCall(
      'reports/sale_order_delivery_report',
      {
        branch_id: branchId,
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
        party_id: PartyId,
        department_id: DepartmentId,
        revenue_account_id: RevenueAccountId,
        status: Status,
        project_id: ProjectId,
      },
      'get',
      (response) => {
        const payload = response?.data
        const reportRows = Array.isArray(payload) ? payload : payload?.data || []
        const reportSummary = Array.isArray(payload)
          ? null
          : payload?.summary || payload?.total_row || null

        setSaleOrderDeliveryReportData(reportRows)
        setSaleOrderDeliveryReportSummary(reportSummary)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setSaleOrderDeliveryReportSummary(null)
        setIsLoading(false)
      },
    )
  }

  const columns = [
    {
      key: 'sr',
      title: 'SR',
      render: (text, record, index) => index + 1, // Adds Serial Number
      fixed: 'left',
      width: 50,
    },
    {
      key: 'id',
      title: 'DO ID',
      dataIndex: 'id',
      fixed: 'left',
      width: 70,
    },
    {
      key: 'code',
      title: 'DO#',
      dataIndex: 'code',
      fixed: 'left',
      width: 90,
    },
    {
      key: 'code',
      title: 'SOD#',
      dataIndex: 'code',
      fixed: 'left',
      width: 90,
      render: (text, record) => record.sale_order.code,
    },
    {
      key: 'party_id',
      title: 'Customer',
      dataIndex: 'party_id',
      render: (text, record) => record.party.name,
      fixed: 'left',
      width: 150,
    },
    {
      key: 'department_id',
      title: 'Department',
      dataIndex: 'department_id',
      fixed: 'left',
      width: 150,
      render: (text, record) => record.sale_order.department.name,
    },
    {
      key: 'delivery_date',
      title: 'DO Date',
      dataIndex: 'delivery_date',
      width: 140,
      render: (delivery_date) => <span>{formatDate(delivery_date)}</span>,
    },
    {
      key: 'total_item_amount',
      title: 'Total Price (PKR)',
      dataIndex: 'total_item_amount',
      width: 140,
      render: (total_item_amount) => renderAmountValue(total_item_amount, 3, summaryColors.totalItemAmount),
    },

    {
      key: 'total_tax',
      title: 'Total Tax (PKR)',
      dataIndex: 'total_tax',
      width: 130,
      render: (total_tax) => renderAmountValue(total_tax, 2, summaryColors.totalTax),
    },
    {
      key: 'additional_tax_amount',
      title: 'Additional Tax (PKR)',
      dataIndex: 'additional_tax_amount',
      width: 150,
      render: (additional_tax_amount) =>
        renderAmountValue(additional_tax_amount ?? 0, 2, summaryColors.additionalTaxAmount),
    },
    {
      key: 'income_tax_amount',
      title: 'Income Tax (PKR)',
      dataIndex: 'income_tax_amount',
      width: 140,
      render: (income_tax_amount) =>
        renderAmountValue(income_tax_amount ?? 0, 2, summaryColors.incomeTaxAmount),
    },

    {
      key: 'discount_amount',
      title: 'Total Discount (PKR)',
      dataIndex: 'discount_amount',
      width: 150,
      render: (discount_amount) =>
        renderAmountValue(discount_amount, 3, summaryColors.discountAmount),
    },
    {
      key: 'do_value',
      title: 'Net Total (PKR)',
      dataIndex: 'do_value',
      width: 140,
      // fixed: 'right', // Last column fixed
      // width: 150,
      render: (do_value) => renderAmountValue(do_value, 2, summaryColors.doValue),
    },
    {
      key: 'branch_id',
      title: 'Branch',
      render: (text, record) => record.branch.name,
      width: 140,
      // fixed: 'left',
      // width: 150,
    },
    {
      key: 'project_id',
      title: 'Project',
      dataIndex: 'project_id',
      render: (text, record) => record.project.name,
      width: 140,
      // fixed: 'left',
      // width: 150,
    },
    {
      key: 'revenue_account_id',
      title: 'Revenue Account',
      dataIndex: 'revenue_account_id',
      width: 160,
      render: (text, record) =>
        record.sale_order.revenue_account ? record.sale_order.revenue_account.name : '',
      // fixed: 'left',
      // width: 150,
    },

    {
      key: 'created_at',
      title: 'Creation Date',
      dataIndex: 'created_at',
      width: 170,
      render: (created_at) => <span>{formatDateTime(created_at)}</span>,
    },
    {
      key: 'created_by_id',
      title: 'Creation By',
      dataIndex: 'created_by_id',
      width: 130,
      render: (text, record) => record.created_by.name,
    },
    {
      key: 'hod_approved_by_id',
      title: 'Approved By',
      dataIndex: 'hod_approved_by_id',
      width: 130,
      render: (text, record) => (record.hod_approved_by ? record.hod_approved_by.name : '-'),
    },
    {
      key: 'hod_approved_at',
      title: 'Approved At',
      dataIndex: 'hod_approved_at',
      width: 170,
      render: (hod_approved_at) => <span>{formatDateTime(hod_approved_at)}</span>,
    },
    {
      key: 'approved_by_id',
      title: 'DO Verified By',
      dataIndex: 'approved_by_id',
      width: 140,
      render: (text, record) => (record.approved_by ? record.approved_by.name : '-'),
    },
    {
      key: 'approved_at',
      title: 'DO Verified At',
      dataIndex: 'approved_at',
      width: 170,
      render: (approved_at) => <span>{formatDateTime(approved_at)}</span>,
    },
  ]

  const summaryCellStyle = {
    textAlign: 'right',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
  }

  const summaryValueStyle = {
    display: 'inline-block',
    width: '100%',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
  }

  const bodyValueStyle = {
    display: 'inline-block',
    width: '100%',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    fontWeight: 600,
  }

  const renderAmountValue = (value, decimals, color) => {
    const numericValue = Number(value) || 0
    return (
      <span style={{ ...bodyValueStyle, color }}>
        {numericValue === 0 ? '-' : wtFormatRoundedValue(numericValue, '', '', decimals)}
      </span>
    )
  }

  const tableScrollX = 2870

  const summaryWidths = {
    totalLabel: 670,
    deliveryDate: 140,
    totalItemAmount: 140,
    totalTax: 130,
    additionalTaxAmount: 150,
    incomeTaxAmount: 140,
    discountAmount: 150,
    doValue: 140,
    branch: 140,
    project: 140,
    revenueAccount: 160,
    createdAt: 170,
    createdBy: 130,
    hodApprovedBy: 130,
    hodApprovedAt: 170,
    approvedBy: 140,
    approvedAt: 170,
  }

  const summaryColors = {
    totalItemAmount: '#0f766e',
    totalTax: '#1d4ed8',
    additionalTaxAmount: '#7c3aed',
    incomeTaxAmount: '#dc2626',
    discountAmount: '#ca8a04',
    doValue: '#2563eb',
  }

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout={'vertical'}
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          style={{ width: '100%' }}
          title={
            <span style={{ fontSize: '26px', marginLeft: '45%' }}>Sale Order Delivery Report</span>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={15} sm={10} md={4}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setDateRange(value)
                  if (value && value.length === 2) {
                    setFromDate(value[0].format('YYYY-MM-DD'))
                    setToDate(value[1].format('YYYY-MM-DD'))
                  }
                }}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>
            <Col xs={15} sm={10} md={5}>
              <label>Department:</label>
              <WidgetDepartmentSelector
                value={DepartmentId}
                displayValue={DepartmentName}
                departments={departments}
                placeholder="Select Department"
                onChange={(values) => changeValues('department_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={15} sm={10} md={5}>
              <label>Revenue Account:</label>
              <WidgetAccountsSelector
                data={accounts}
                value={RevenueAccountId}
                displayValue={RevenueAccountName}
                onChange={(value, label) => {
                  changeValues('revenue_account_id', value)
                  if (typeof label === 'string') {
                    setRevenueAccountName(label)
                  } else if (Array.isArray(label) && label.length > 0) {
                    setRevenueAccountName(label[label.length - 1] || '')
                  } else if (!value) {
                    setRevenueAccountName('')
                  }
                }}
                reloadDropdownData={() => Base.dropdownData('accounts', {}, [setAccounts], 1)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={15} sm={10} md={5}>
              <label>Customer</label>
              <WidgetCustomerSelector
                onChange={(value) => changeValues('customer_id', value)}
                customers={customers}
                displayValue={PartyName}
                value={PartyId}
                reloadDropdownData={() => reloadParties()}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Button
                style={{ marginTop: 30, marginRight: 5, color: '#fff', backgroundColor: '#E29C06' }}
                onClick={() => setSearchMore(!searchMore)}
                disabled={isLoading ? true : false}
              >
                {searchMore ? 'Less...' : 'More...'}
              </Button>
              <Button
                style={{ marginTop: 30, marginRight: 5 }}
                type="primary"
                htmlType="submit"
                disabled={isLoading ? true : false}
              >
                Search
              </Button>
              <Button
                style={{ marginTop: 30, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading ? true : false}
              >
                Export
              </Button>
              <Button
                style={{ marginTop: 30, marginLeft: 5, backgroundColor: '#1677ff', color: '#fff' }}
                onClick={printDoPdf}
                disabled={isLoading || SoDData.length === 0}
              >
                {pdfProgress ? `${pdfProgress.stage} ${pdfProgress.current}/${pdfProgress.total}` : 'DO PDF'}
              </Button>
            </Col>
          </Row>
          {searchMore && (
            <>
              <br></br>
              <Row gutter={[24, 24]}>
                <Col xs={15} sm={10} md={4}>
                  <label>Status:</label>
                  <WidgetSelector
                    options={SALE_ORDER_STATUS_DROPDOWN_OPTIONS}
                    selectedValue={Status}
                    displayValue={SALE_ORDER_STATUS_NAME[Status]}
                    onChange={(value) => changeValues('status', value)}
                  />
                </Col>
                <Col xs={15} sm={10} md={5}>
                  <label>Project:</label>
                  <WidgetProjectSelector
                    onChange={(value) => changeValues('project_id', value)}
                    data={projects} // <-- Use the formatted treeData here
                    value={ProjectId}
                    displayValue={ProjectName}
                    height={35}
                    reloadDropdownData={() => Base.dropdownData('projects', {}, [setProjects], 1)}
                  />
                </Col>
              </Row>
            </>
          )}
        </Card>
      </Form>
      <Card style={{ marginTop: 30 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={SoDData}
            rowKey="id"
            pagination={false}
            bordered
            tableLayout="fixed"
            scroll={{ x: tableScrollX, y: 400 }}
            summary={() =>
              SoDSummary ? (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={5}
                      style={{
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 'bold',
                        width: summaryWidths.totalLabel,
                        minWidth: summaryWidths.totalLabel,
                        maxWidth: summaryWidths.totalLabel,
                      }}
                    >
                      Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={1}
                      style={{ width: summaryWidths.deliveryDate, minWidth: summaryWidths.deliveryDate, maxWidth: summaryWidths.deliveryDate }}
                    />
                    <Table.Summary.Cell
                      index={2}
                      style={{ ...summaryCellStyle, width: summaryWidths.totalItemAmount, minWidth: summaryWidths.totalItemAmount, maxWidth: summaryWidths.totalItemAmount }}
                    >
                      {renderAmountValue(SoDSummary.total_item_amount, 0, summaryColors.totalItemAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={3}
                      style={{ ...summaryCellStyle, width: summaryWidths.totalTax, minWidth: summaryWidths.totalTax, maxWidth: summaryWidths.totalTax }}
                    >
                      {renderAmountValue(SoDSummary.total_tax, 0, summaryColors.totalTax)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={4}
                      style={{ ...summaryCellStyle, width: summaryWidths.additionalTaxAmount, minWidth: summaryWidths.additionalTaxAmount, maxWidth: summaryWidths.additionalTaxAmount }}
                    >
                      {renderAmountValue(
                        SoDSummary.additional_tax_amount,
                        0,
                        summaryColors.additionalTaxAmount,
                      )}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      style={{ ...summaryCellStyle, width: summaryWidths.incomeTaxAmount, minWidth: summaryWidths.incomeTaxAmount, maxWidth: summaryWidths.incomeTaxAmount }}
                    >
                      {renderAmountValue(SoDSummary.income_tax_amount, 0, summaryColors.incomeTaxAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={6}
                      style={{ ...summaryCellStyle, width: summaryWidths.discountAmount, minWidth: summaryWidths.discountAmount, maxWidth: summaryWidths.discountAmount }}
                    >
                      {renderAmountValue(SoDSummary.discount_amount, 0, summaryColors.discountAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      style={{ ...summaryCellStyle, width: summaryWidths.doValue, minWidth: summaryWidths.doValue, maxWidth: summaryWidths.doValue }}
                    >
                      {renderAmountValue(SoDSummary.do_value, 0, summaryColors.doValue)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      style={{ width: summaryWidths.branch, minWidth: summaryWidths.branch, maxWidth: summaryWidths.branch }}
                    />
                    <Table.Summary.Cell
                      index={9}
                      style={{ width: summaryWidths.project, minWidth: summaryWidths.project, maxWidth: summaryWidths.project }}
                    />
                    <Table.Summary.Cell
                      index={10}
                      style={{ width: summaryWidths.revenueAccount, minWidth: summaryWidths.revenueAccount, maxWidth: summaryWidths.revenueAccount }}
                    />
                    <Table.Summary.Cell
                      index={11}
                      style={{ width: summaryWidths.createdAt, minWidth: summaryWidths.createdAt, maxWidth: summaryWidths.createdAt }}
                    />
                    <Table.Summary.Cell
                      index={12}
                      style={{ width: summaryWidths.createdBy, minWidth: summaryWidths.createdBy, maxWidth: summaryWidths.createdBy }}
                    />
                    <Table.Summary.Cell
                      index={13}
                      style={{ width: summaryWidths.hodApprovedBy, minWidth: summaryWidths.hodApprovedBy, maxWidth: summaryWidths.hodApprovedBy }}
                    />
                    <Table.Summary.Cell
                      index={14}
                      style={{ width: summaryWidths.hodApprovedAt, minWidth: summaryWidths.hodApprovedAt, maxWidth: summaryWidths.hodApprovedAt }}
                    />
                    <Table.Summary.Cell
                      index={15}
                      style={{ width: summaryWidths.approvedBy, minWidth: summaryWidths.approvedBy, maxWidth: summaryWidths.approvedBy }}
                    />
                    <Table.Summary.Cell
                      index={16}
                      style={{ width: summaryWidths.approvedAt, minWidth: summaryWidths.approvedAt, maxWidth: summaryWidths.approvedAt }}
                    />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        </Spin>
      </Card>
    </div>
  )
}
export default SaleOrderDeliveryReport
